import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import { applySeedlessStart } from '../utils/pathHome';

/**
 * StTalkHome — the storyteller's doorway (F-80).
 * Implements the ratified Claude Design handoff "Talebound Doorway.dc.html", direction 2a
 * ("refined heirloom title page"): a quiet wordmark, Pip face-to-face, one focal "Start talking"
 * button, a ghost "Pick a topic", the consent reassurance line, and a gift link.
 *
 * Mounts INSIDE the app shell, which already provides the bottom nav — so this renders ONLY the
 * doorway's own content (no device status bar, no nav bar). Real in-system tokens: Lora, cream
 * (#FBF8F1 / #FFFDF8), Pip-orange (#A85A0F / #AE6015 gradient), #92400E links, #B8813E borders.
 * Real flat-cartoon Pip asset (/pip/pip-body.png).
 *
 * Wiring:
 *  - "Start talking" (primary) -> SEEDLESS entry: easy-start pack + null prompt + seedlessStart handshake
 *    so StRecordPage enters the voice interview directly (cold-start opener + F-62 warm-up ramp + F-54).
 *    No forced pack picker.
 *  - "Pick a topic" (ghost) -> today's existing pack->prompt flow, UNCHANGED (forcePackPicker).
 *  - gift link -> the existing in-app gift entry (openGiftSheet). [FLAG: founder confirms destination.]
 */
const StTalkHome = () => {
  const {
    setActiveTab, setStStartMode, setStInterviewMode, setStInterviewStep,
    setStSelectedPrompt, setForcePackPicker, setActivePackId, setSeedlessStart, setPathOpenPackId, openGiftSheet,
  } = useAppContext();

  // Primary: seedless "Start talking". Enter the voice interview with no chosen topic so Pip opens
  // on the cold-start hello and warms up from there. easy-start is the warm first-timer pack config.
  const startTalking = () => applySeedlessStart({
    setStStartMode, setActivePackId, setStSelectedPrompt, setStInterviewMode,
    setForcePackPicker, setSeedlessStart, setPathOpenPackId, setActiveTab,
  });

  // Secondary: the existing "pick a topic first" flow, unchanged (forces the pack chooser).
  const pickTopic = () => {
    if (setStStartMode) setStStartMode('text');
    if (setStInterviewMode) setStInterviewMode(false);
    if (setStInterviewStep) setStInterviewStep('pick');
    if (setStSelectedPrompt) setStSelectedPrompt(null);
    if (setForcePackPicker) setForcePackPicker(true);
    setActiveTab('st-record');
  };

  const openGift = () => { try { if (openGiftSheet) openGiftSheet('doorway'); } catch (e) { /* never break the doorway */ } };

  return (
    <div className="tb-door">
      <style>{`
        .tb-door {
          min-height: calc(100vh - 150px);
          display: flex; flex-direction: column; align-items: center;
          padding: 14px 18px 8px;
          background: #FBF8F1;
          color: #0F172A;
          font-family: 'Lora', Georgia, serif;
        }
        /* The heirloom card — the doorway's focal surface. Mobile-first; centered + capped on desktop. */
        .tb-card {
          width: 100%; max-width: 430px; flex: 1;
          display: flex; flex-direction: column; align-items: center; text-align: center;
          background: #FFFDF8;
          border: 1px solid rgba(184,129,62,.32);
          border-radius: 22px;
          box-shadow: 0 2px 12px rgba(15,23,42,.04);
          padding: 22px 26px 18px;
          box-sizing: border-box;
        }
        @media (min-width: 641px) {
          .tb-door { justify-content: center; }
          .tb-card { flex: none; min-height: 640px; margin-top: 8px; }
        }
        .tb-wordmark { display: flex; align-items: center; gap: 10px; color: #B08A57; }
        .tb-wordmark .rule { width: 22px; height: 1px; background: rgba(184,129,62,.55); }
        .tb-wordmark .tb-name { font-size: 12.5px; letter-spacing: .3em; text-transform: uppercase; font-weight: 600; }
        .tb-pipwrap { margin-top: 10px; width: 224px; height: 160px; display: flex; align-items: flex-end; justify-content: center; }
        .tb-pip { height: 160px; width: auto; display: block; filter: drop-shadow(0 8px 14px rgba(120,70,20,.16)); }
        .tb-hi { margin: 12px 0 0; font-size: 16px; font-weight: 600; font-style: italic; color: #A85A0F; }
        .tb-h1 { margin: 8px 0 0; font-size: 32px; line-height: 1.2; font-weight: 600; font-style: italic; letter-spacing: -.01em; text-wrap: pretty; }
        .tb-sub { margin: 12px 0 0; font-size: 17px; line-height: 1.5; color: #3C4653; max-width: 302px; text-wrap: pretty; }
        .tb-primary {
          width: 100%; margin-top: 20px;
          background: linear-gradient(180deg,#AE6015,#A85A0F); color: #fff;
          border: none; border-radius: 18px; padding: 20px;
          font-family: 'Lora', serif; font-size: 22px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; gap: 13px;
          box-shadow: 0 4px 16px rgba(168,90,15,.3); cursor: pointer;
          -webkit-tap-highlight-color: transparent; transition: transform .1s ease;
        }
        .tb-primary:active { transform: scale(.98); }
        .tb-bars { display: inline-flex; align-items: flex-end; gap: 3px; height: 22px; }
        .tb-bars span { width: 3px; background: #fff; border-radius: 2px; }
        .tb-reassure { margin: 11px 6px 0; font-size: 16px; line-height: 1.5; color: #33404F; text-wrap: pretty; }
        .tb-ghost {
          width: 100%; margin-top: 13px;
          background: #FFFDF8; color: #92400E; border: 1px solid #B8813E; border-radius: 16px;
          padding: 15px; font-family: 'Lora', serif; font-size: 17px; font-weight: 600; cursor: pointer;
          -webkit-tap-highlight-color: transparent; transition: transform .1s ease;
        }
        .tb-ghost:active { transform: scale(.98); }
        .tb-spacer { flex: 1; min-height: 10px; }
        .tb-consent { display: flex; align-items: center; justify-content: center; gap: 9px; margin-top: 16px; color: #33404F; font-size: 16px; line-height: 1.4; }
        .tb-gift {
          background: none; border: none; cursor: pointer;
          text-align: center; font-size: 16px; font-style: italic; color: #92400E;
          text-decoration: underline; text-underline-offset: 3px;
          padding: 15px 20px 10px; font-family: 'Lora', serif;
        }
      `}</style>

      <div className="tb-card">
        <div className="tb-wordmark">
          <span className="rule" />
          <span className="tb-name">Talebound</span>
          <span className="rule" />
        </div>

        <div className="tb-pipwrap">
          <img className="tb-pip" src="/pip/pip-body.png" alt="Pip the fox" />
        </div>

        <p className="tb-hi">Hi, I&rsquo;m Pip.</p>
        <h1 className="tb-h1">Let&rsquo;s write one story.</h1>
        <p className="tb-sub">Just talk with me, and your words become a real book your family can hold.</p>

        <button className="tb-primary" onClick={startTalking}>
          <span className="tb-bars" aria-hidden="true">
            <span style={{ height: 10, opacity: .9 }} />
            <span style={{ height: 20 }} />
            <span style={{ height: 14, opacity: .9 }} />
            <span style={{ height: 22 }} />
            <span style={{ height: 9, opacity: .9 }} />
          </span>
          Start talking
        </button>
        <p className="tb-reassure">Like talking with a friend. No need to pick a question; just start anywhere.</p>

        <button className="tb-ghost" onClick={pickTopic}>Pick a topic</button>

        <div className="tb-spacer" />

        <div className="tb-consent">
          {/* F-84: a quiet line-style padlock, muted to the text color (decorative, not interactive). */}
          <svg width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden="true" style={{ flex: 'none', opacity: .7 }}>
            <rect x="2" y="6.6" width="10" height="7.4" rx="1.5" stroke="#33404F" strokeWidth="1.3" />
            <path d="M4 6.6V4.6a3 3 0 0 1 6 0v2" stroke="#33404F" strokeWidth="1.3" />
          </svg>
          Nothing goes in your book until you choose to keep it.
        </div>
      </div>

      <button className="tb-gift" onClick={openGift}>Making a book to give someone?</button>
    </div>
  );
};

export default StTalkHome;
